/**
 * Files an agent made during a run (agion/Agion#2003).
 *
 * A tool result names the files it produced; chat-ui's chat shows each one as
 * a file card (OutputFileBlock). Both ways a tool result reaches the chat map
 * through this module, so a card is the same live and after a reload:
 * - live: mission.tool.result carries output_files (useStreamingMissionChat);
 * - reload: the stored tool row holds the tool's JSON text, and the files are
 *   read from it the way mission-runner's extractOutputFiles reads them
 *   (outputFilesFromStoredToolOutput).
 *
 * The admin-ui Mission Designer run console maps each live mission.tool.result
 * through toolResultViewFiles(): its output_files, or the files its output
 * lists, read like a stored row.
 *
 * A file entry without a file id or a name is left out: the card could not
 * download it. A generated tool whose file registration failed reports such
 * entries.
 */

import type { ToolResultView } from './generated/missionstream'

/** A file a tool made, as the chat message carries it and the file card shows it. */
export interface ToolOutputFile {
  name: string
  fileId: string
  size: number
  contentType: string
  downloadUrl: string
}

type JsonObject = Record<string, unknown>

function isObject(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}

/** One snake_case entry ({name, file_id, size, content_type, download_url}), or null when it names no downloadable file. */
function toToolOutputFile(entry: unknown): ToolOutputFile | null {
  if (!isObject(entry)) return null
  const { name, file_id: fileId, size, content_type: contentType, download_url: downloadUrl } = entry
  if (!isNonEmptyString(name) || !isNonEmptyString(fileId)) return null
  return {
    name,
    fileId,
    size: typeof size === 'number' && Number.isFinite(size) ? size : 0,
    contentType: typeof contentType === 'string' ? contentType : '',
    downloadUrl: typeof downloadUrl === 'string' ? downloadUrl : '',
  }
}

/**
 * The files of a snake_case file list, as the wire (output_files) and the
 * stored tool JSON (_output_files) carry it. Undefined when there is no list.
 */
export function toToolOutputFiles(entries: unknown): ToolOutputFile[] | undefined {
  if (!Array.isArray(entries)) return undefined
  const files: ToolOutputFile[] = []
  for (const entry of entries) {
    const file = toToolOutputFile(entry)
    if (file) files.push(file)
  }
  return files
}

function isToolOutputFile(value: unknown): value is ToolOutputFile {
  return isObject(value) && isNonEmptyString(value['name']) && isNonEmptyString(value['fileId'])
}

/** The files a tool_result chat message carries (data['outputFiles']). */
export function toolResultFiles(data: unknown): ToolOutputFile[] {
  if (!isObject(data) || !Array.isArray(data['outputFiles'])) return []
  return data['outputFiles'].filter(isToolOutputFile)
}

/** Text that does not start like JSON: a plain-text tool output. */
const PLAIN_TEXT = Symbol('plain text')
/** Text that starts like JSON but cannot be parsed, such as a cut-off row. */
const BROKEN_JSON = Symbol('broken JSON')

function parseJsonText(text: string): unknown {
  const first = text.trim().charAt(0)
  if (first !== '{' && first !== '[' && first !== '"') return PLAIN_TEXT
  try {
    return JSON.parse(text)
  } catch {
    return BROKEN_JSON
  }
}

interface StoredFiles {
  files: ToolOutputFile[]
  /** Why files the row names cannot all be shown. */
  problem?: string
}

/** The file list of a tool's JSON: output['_output_files'] before the top-level _output_files, the first that is not empty. */
function readFileList(toolJson: JsonObject): StoredFiles {
  const generated = isObject(toolJson['output']) ? toolJson['output']['_output_files'] : undefined
  const builtIn = toolJson['_output_files']
  for (const list of [generated, builtIn]) {
    if (!Array.isArray(list) || list.length === 0) continue
    const files = toToolOutputFiles(list) ?? []
    const leftOut = list.length - files.length
    if (leftOut === 0) return { files }
    return { files, problem: `${leftOut} of ${list.length} file entries have no file id or name` }
  }
  const unreadable = [generated, builtIn].some((list) => list !== undefined && list !== null && !Array.isArray(list))
  return unreadable ? { files: [], problem: '_output_files is not a list' } : { files: [] }
}

function readStoredFiles(content: unknown): StoredFiles {
  if (typeof content !== 'string') return { files: [] }
  let value = parseJsonText(content)
  // ChatReporter stores json.Marshal(output): the tool's JSON text as a JSON string.
  if (typeof value === 'string') value = parseJsonText(value)
  if (value === BROKEN_JSON) return { files: [], problem: 'its text is not valid JSON' }
  return isObject(value) ? readFileList(value) : { files: [] }
}

/**
 * The files a stored tool row names (role 'tool' in the chat history). A row
 * whose files cannot all be read gives the ones it can, never throws, and
 * logs one warning with the row id. A row without text names no files.
 */
export function outputFilesFromStoredToolOutput(content: unknown, rowId: string): ToolOutputFile[] {
  const { files, problem } = readStoredFiles(content)
  if (problem) console.warn(`[tool-output-files] Cannot read files from tool result row ${rowId}: ${problem}.`)
  return files
}

/**
 * The files of a mission.tool.result view: its output_files, or, when those
 * name no downloadable file, the files its output lists under _output_files,
 * read the way a stored row is read (output['_output_files'] before the
 * top-level _output_files). A host that is not updated sends the files only in
 * the output. The output is the tool's JSON text or the object the host sent;
 * an output cut short at the host's cap names no files. Never throws.
 */
export function toolResultViewFiles(view: Pick<ToolResultView, 'output_files' | 'output'>): ToolOutputFile[] {
  const sent = toToolOutputFiles(view.output_files) ?? []
  if (sent.length > 0) return sent
  if (typeof view['output'] === 'string') return readStoredFiles(view['output']).files
  return isObject(view['output']) ? readFileList(view['output']).files : []
}

/**
 * A file's download_url is the Edge host's own address (agion-mcp writes
 * http://127.0.0.1:8080/v1/files/<id>/download, generated tools
 * /v1/files/<id>/download). It resolves to nothing in the user's browser, and
 * the apps download by host and file id through the governance proxy
 * (agion/Agion#2003), so an output shown to a user never carries it.
 */
const DOWNLOAD_URL_KEY = 'download_url'

/** A file list with each entry's download_url left out; the same list when none has one. */
function withoutDownloadUrls(list: unknown): unknown {
  if (!Array.isArray(list) || !list.some((entry) => isObject(entry) && DOWNLOAD_URL_KEY in entry)) return list
  return list.map((entry) => {
    if (!isObject(entry) || !(DOWNLOAD_URL_KEY in entry)) return entry
    const { [DOWNLOAD_URL_KEY]: _address, ...rest } = entry
    return rest
  })
}

/** A tool's JSON with download_url left out of both file lists readFileList reads; the same object when there is none. */
function toolJsonForDisplay(toolJson: JsonObject): JsonObject {
  let shown = toolJson
  const builtIn = withoutDownloadUrls(toolJson['_output_files'])
  if (builtIn !== toolJson['_output_files']) shown = { ...shown, _output_files: builtIn }
  if (isObject(toolJson['output'])) {
    const generated = withoutDownloadUrls(toolJson['output']['_output_files'])
    if (generated !== toolJson['output']['_output_files']) shown = { ...shown, output: { ...toolJson['output'], _output_files: generated } }
  }
  return shown
}

// "download_url": "..." as written in JSON text, or inside a JSON string
// (\"download_url\":\"...\"), with a following comma; a value cut off at the
// end of the text is matched too.
const DOWNLOAD_URL_MEMBER = /"download_url"\s*:\s*"(?:[^"\\]|\\.)*(?:"\s*,?|$)/g
const ESCAPED_DOWNLOAD_URL_MEMBER = /\\"download_url\\"\s*:\s*\\"(?:[^"\\]|\\[^"])*(?:\\"\s*,?|$)/g

/**
 * A tool output as a user reads it in the Activity log: the tool's own text,
 * with each produced file's download_url left out, since that address is the
 * Edge host's own. JSON text comes back as compact JSON text, a stored history
 * row (JSON text encoded as a JSON string) as the inner JSON text, and an
 * object as a copy; the message it came from is not changed. An output cut
 * short at the host's cap loses the member by pattern. A download_url outside
 * a file list, such as a web link a tool returns, stays, and output that names
 * no file's address is returned as it is. Never throws.
 */
export function toolOutputForDisplay(output: unknown): unknown {
  if (isObject(output)) return toolJsonForDisplay(output)
  if (typeof output !== 'string' || !output.includes(DOWNLOAD_URL_KEY)) return output
  let value = parseJsonText(output)
  if (typeof value === 'string') value = parseJsonText(value)
  if (isObject(value)) {
    const shown = toolJsonForDisplay(value)
    return shown === value ? output : JSON.stringify(shown)
  }
  if (value !== BROKEN_JSON) return output
  return output.replace(ESCAPED_DOWNLOAD_URL_MEMBER, '').replace(DOWNLOAD_URL_MEMBER, '')
}
